import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CreateResortModal from "./createPropertyModal";
import EditResortModal from "./EditResortModal";
import axios from "axios";
import {
  HomeIcon,
  PencilIcon,
  TrashIcon,
  PhoneIcon,
  MailIcon,
  OfficeBuildingIcon,
  PlusIcon,
  ViewGridIcon,
  ViewListIcon,
  SearchIcon,
  UploadIcon,
} from "@heroicons/react/outline";

const Resort = () => {
  const navigate = useNavigate();
  const [selectedResortId, setSelectedResortId] = useState(null);
  const isPropertyUser = (() => {
    try {
      const role = JSON.parse(localStorage.getItem("user") || "null")?.role?.role_name;
      return role === "resort-supervisor" || role === "resort-manager";
    } catch (error) {
      return false;
    }
  })();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [resorts, setResorts] = useState([]);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState("");

  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const openEditModal = (resortId) => {
    setSelectedResortId(resortId);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setSelectedResortId(null);
    setIsEditModalOpen(false);
  };

  const handleDeleteResort = async (resortId, e) => {
    e.stopPropagation(); // Prevent row click event

    if (window.confirm("Are you sure you want to delete this property?")) {
      try {
        const accessToken = localStorage.getItem("accessToken");
        await axios.delete(`${baseUrl}/resort/${resortId}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        toast.success("Property deleted successfully");
        fetchResorts(); // Refresh the list
      } catch (error) {
        toast.error("Failed to delete property");
        console.error("Error deleting resort:", error);
      }
    }
  };

  const fetchResorts = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await axios.get(`${baseUrl}/resort`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.data && Array.isArray(response.data.data.resorts)) {
        console.log("Fetched Resorts:", response.data.data.resorts);
        setResorts(response.data.data.resorts);
      } else {
        console.error("Unexpected response data:", response.data);
      }
    } catch (error) {
      console.error("Error fetching resorts:", error);
      toast.error("Failed to fetch properties");
    }
  }, [baseUrl]);

  useEffect(() => {
    fetchResorts();
  }, [baseUrl, fetchResorts]);

  const handleResortRoute = async (resort_id) => {
    console.log("Clicked Resort ID:", resort_id);
    navigate(`/dashboard/view-property/${resort_id}`);
  };

  // Filter resorts based on search query
  const filteredResorts = resorts.filter((resort) =>
    resort.resort_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resort.resort_description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resort.resort_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Property gradient colors
  const propertyGradients = [
    "from-amber-400 to-orange-500",
    "from-emerald-400 to-teal-500",
    "from-blue-400 to-indigo-500",
    "from-cyan-400 to-blue-500",
    "from-green-400 to-emerald-500",
    "from-purple-400 to-pink-500",
    "from-rose-400 to-red-500",
    "from-violet-400 to-purple-500",
  ];

  const propertyEmojis = ["🏖️", "⛰️", "🏙️", "🌴", "🌾", "✨", "🏨", "🏝️"];

  return (
    <div className="min-h-screen">
      {/* Header Section */}
      <div className="mb-8">
        {/* Breadcrumb */}
        <nav className="flex mb-4" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link
                to="/dashboard"
                className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-purple-600 transition-colors duration-200"
              >
                <HomeIcon className="w-4 h-4 mr-2" />
                Dashboard
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="w-3 h-3 text-gray-400 mx-1"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 6 10"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m1 9 4-4-4-4"
                  />
                </svg>
                <span className="ml-1 text-sm font-bold text-gray-800 md:ml-2">
                  Properties
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Property Management
            </h1>
            <p className="text-gray-600">
              Manage and monitor all your properties in one place
            </p>
          </div>
          {!isPropertyUser && (
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate("/dashboard/property/import")} className="inline-flex items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm font-bold text-indigo-700 transition hover:bg-indigo-100">
                <UploadIcon className="h-5 w-5" />
                <span>Import configuration</span>
              </button>
              <button onClick={openCreateModal} className="create-property-btn">
                <PlusIcon className="w-5 h-5" />
                <span>Create New Property</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters and View Toggle */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search properties..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
            />
          </div>

          {/* View Toggle and Stats */}
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg px-4 py-2">
              <span className="text-sm font-semibold text-purple-700">
                {filteredResorts.length} {filteredResorts.length === 1 ? "Property" : "Properties"}
              </span>
            </div>

            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-md transition-all duration-200 ${
                  viewMode === "grid"
                    ? "bg-white shadow-sm text-purple-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <ViewGridIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-md transition-all duration-200 ${
                  viewMode === "list"
                    ? "bg-white shadow-sm text-purple-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <ViewListIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Properties Display */}
      {viewMode === "grid" ? (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredResorts.length > 0 ? (
            filteredResorts.map((resort, idx) => (
              <div
                key={resort.resort_id}
                className="property-card"
                onClick={() => handleResortRoute(resort.resort_id)}
              >
                {/* Card Header with Gradient */}
                <div
                  className={`h-32 bg-gradient-to-r ${
                    propertyGradients[idx % propertyGradients.length]
                  } relative overflow-hidden cursor-pointer`}
                >
                  <div className="absolute inset-0 bg-black/20 hover:bg-black/10 transition-all duration-300"></div>
                  <div className="absolute inset-0 flex items-center justify-center text-6xl property-emoji">
                    {propertyEmojis[idx % propertyEmojis.length]}
                  </div>
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <div className="flex items-center space-x-1">
                      <OfficeBuildingIcon className="w-4 h-4 text-gray-700" />
                      <span className="text-sm font-semibold text-gray-700">
                        Property
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-5">
                  <h3 className="text-lg font-bold text-gray-800 mb-3 property-title">
                    {resort.resort_name}
                  </h3>

                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {resort.resort_description || "No description available"}
                  </p>

                  {/* Property Info */}
                  <div className="space-y-2 mb-4">
                    {resort.resort_phone && (
                      <div className="flex items-center text-sm text-gray-600">
                        <PhoneIcon className="w-4 h-4 mr-2 text-green-500" />
                        <span>{resort.resort_phone}</span>
                      </div>
                    )}
                    {resort.resort_email && (
                      <div className="flex items-center text-sm text-gray-600">
                        <MailIcon className="w-4 h-4 mr-2 text-blue-500" />
                        <span className="truncate">{resort.resort_email}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  {!isPropertyUser && (
                    <div className="flex gap-2 pt-4 border-t border-gray-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(resort.resort_id);
                        }}
                        className="action-btn action-btn-edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={(e) => handleDeleteResort(resort.resort_id, e)}
                        className="action-btn action-btn-delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full">
              <div className="empty-state">
                <OfficeBuildingIcon className="w-16 h-16 text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  No Properties Found
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchQuery
                    ? "Try adjusting your search criteria"
                    : "Get started by creating your first property"}
                </p>
                {!searchQuery && !isPropertyUser && (
                  <button onClick={openCreateModal} className="create-property-btn">
                    <PlusIcon className="w-5 h-5" />
                    <span>Create New Property</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Property Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Contact
                  </th>
                  {!isPropertyUser && (
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredResorts.length > 0 ? (
                  filteredResorts.map((resort, idx) => (
                    <tr
                      key={resort.resort_id}
                      onClick={() => handleResortRoute(resort.resort_id)}
                      className="table-row"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div
                            className={`w-10 h-10 rounded-lg bg-gradient-to-br ${
                              propertyGradients[idx % propertyGradients.length]
                            } flex items-center justify-center text-2xl mr-3 flex-shrink-0`}
                          >
                            {propertyEmojis[idx % propertyEmojis.length]}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-800">
                              {resort.resort_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600 max-w-xs truncate">
                          {resort.resort_description || "No description"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {resort.resort_phone && (
                            <div className="flex items-center text-sm text-gray-600">
                              <PhoneIcon className="w-3 h-3 mr-1 text-green-500" />
                              <span>{resort.resort_phone}</span>
                            </div>
                          )}
                          {resort.resort_email && (
                            <div className="flex items-center text-sm text-gray-600">
                              <MailIcon className="w-3 h-3 mr-1 text-blue-500" />
                              <span>{resort.resort_email}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {!isPropertyUser && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditModal(resort.resort_id);
                                }}
                                className="table-action-btn table-action-btn-edit"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => handleDeleteResort(resort.resort_id, e)}
                                className="table-action-btn table-action-btn-delete"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={isPropertyUser ? 3 : 4} className="px-6 py-12">
                      <div className="empty-state">
                        <OfficeBuildingIcon className="w-16 h-16 text-gray-400 mb-4" />
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">
                          No Properties Found
                        </h3>
                        <p className="text-gray-600">
                          {searchQuery
                            ? "Try adjusting your search criteria"
                            : "Get started by creating your first property"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      <CreateResortModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        onResortCreated={fetchResorts}
      />
      <EditResortModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        selectedResortId={selectedResortId}
        fetchResorts={fetchResorts}
      />

      {/* Inline Styles */}
      <style jsx>{`
        .create-property-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          color: white;
          font-weight: 600;
          border-radius: 0.75rem;
          box-shadow: 0 4px 6px -1px rgba(139, 92, 246, 0.3);
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
        }

        .create-property-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.4);
          background: linear-gradient(135deg, #7c3aed, #4f46e5);
        }

        .property-card {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          overflow: hidden;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .property-card:hover {
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          transform: translateY(-4px);
          border-color: #a855f7;
        }

        .property-emoji {
          transition: transform 0.3s ease;
        }

        .property-card:hover .property-emoji {
          transform: scale(1.1);
        }

        .property-title {
          transition: color 0.3s ease;
        }

        .property-card:hover .property-title {
          color: #8b5cf6;
        }

        .action-btn {
          flex: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          font-size: 0.875rem;
          font-weight: 500;
          border-radius: 0.5rem;
          transition: all 0.2s ease;
          border: none;
          cursor: pointer;
        }

        .action-btn-edit {
          background: #eff6ff;
          color: #2563eb;
        }

        .action-btn-edit:hover {
          background: #dbeafe;
          transform: translateY(-1px);
        }

        .action-btn-delete {
          background: #fef2f2;
          color: #dc2626;
        }

        .action-btn-delete:hover {
          background: #fee2e2;
          transform: translateY(-1px);
        }

        .table-row {
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .table-row:hover {
          background-color: #f9fafb;
        }

        .table-action-btn {
          padding: 0.5rem;
          border-radius: 0.5rem;
          transition: all 0.2s ease;
          border: none;
          cursor: pointer;
        }

        .table-action-btn-edit {
          background: #eff6ff;
          color: #2563eb;
        }

        .table-action-btn-edit:hover {
          background: #dbeafe;
          transform: scale(1.1);
        }

        .table-action-btn-delete {
          background: #fef2f2;
          color: #dc2626;
        }

        .table-action-btn-delete:hover {
          background: #fee2e2;
          transform: scale(1.1);
        }

        .empty-state {
          text-align: center;
          padding: 3rem 1rem;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default Resort;
